import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import * as Yup from 'yup';

import { clockIn, clockOut } from '../../models/timeclock/entry/entry';
import toast from '../../utils/toast';

/**
 * @typedef {object} useTimeClockModalOptions
 * @prop { import('../../models/user/_types').User } user
 * @prop { () => void } onSuccessCallback
 * @prop { string } notes
 */

export const useTimeClock = (
  /**@type {useTimeClockModalOptions}**/
  { user, onSuccessCallback, notes }
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('translation', { keyPrefix: 'timetracking' });

  /** @typedef {object} clockInMutationFnOptions
   * @prop {import('types/utilityTypes').Identifier} taskId
   * @prop {string} notes
   */
  const clockInLogEntryMutation = useMutation({
    mutationFn: (/**@type {clockInMutationFnOptions}**/ options) => {
      if (!user?.id) {
        throw new Error('Attempted user update without ID');
      }
      if (!options.taskId) {
        throw new Error('Task ID is required');
      }
      return clockIn({
        taskId: options.taskId,
        userId: user.id,
        notes: options.notes,
      });
    },
    onSuccess: () => {
      toast.success('Clock updated');
      queryClient.invalidateQueries('entries');
      onSuccessCallback();
    },
    onError: () => {
      toast.error('Clock could not be updated');
    },
  });

  /** @typedef {object} clockOutMutationFnOptions
   * @prop {string} notes
   */
  const clockOutLogEntryMutation = useMutation({
    mutationFn: (/**@type {clockOutMutationFnOptions}**/ options) => {
      if (!user?.id) {
        throw new Error('Attempted user update without ID');
      }
      return clockOut(user, options.notes);
    },
    onSuccess: () => {
      toast.success('Clock updated');
      queryClient.invalidateQueries('entries');
      onSuccessCallback();
    },
    onError: () => {
      toast.error('Clock could not be updated');
    },
  });

  const formikClockOut = {
    initialValues: {
      notes: notes,
    },
    validationSchema: Yup.object({
      notes: Yup.string().max(255),
    }),
    onSubmit: async (values, { resetForm }) => {
      await clockOutLogEntryMutation.mutateAsync({ ...values });
      resetForm();
    },
  };

  const formikClockIn = {
    initialValues: {
      taskId: '',
      notes: notes,
    },
    validationSchema: Yup.object({
      taskId: Yup.number().required(t('Task selection is required')),
      notes: Yup.string().max(255),
    }),
    onSubmit: async (values, { resetForm }) => {
      await clockInLogEntryMutation.mutateAsync({ ...values });
      resetForm();
    },
  };

  return {
    formikClockIn,
    formikClockOut,
  };
};
